import React, { useMemo } from 'react';
import { PostCard } from './PostCard';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { Pagination } from '../common/Pagination';
import type { Post } from '../../types/post';
import { useLanguage } from '../../i18n/useLanguage';
import { formatTimelineGroupLabel, groupPostsByTimelineBucket } from '../../utils/dateUtils';

interface PostListProps {
  posts: Post[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onRefreshList?: () => void;
  showComments?: boolean;
}

export const PostList: React.FC<PostListProps> = ({
  posts,
  loading,
  error,
  pagination,
  onPageChange,
  onPageSizeChange,
  onRefreshList,
  showComments = false,
}) => {
  const { t, language } = useLanguage();
  const groupedPosts = useMemo(() => groupPostsByTimelineBucket(posts), [posts]);

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (posts.length === 0) {
    return (
      <div className="post-list-empty">
        <p>{t('post.noneFound')}</p>
      </div>
    );
  }

  return (
    <div className="post-list post-list--timeline">
      <div className="post-list-items post-list-items--timeline">
        <div className="post-timeline-feed">
          <div className="post-timeline-rail-column" aria-hidden="true">
            <div className="post-timeline-line-track" />
          </div>
          <div className="post-timeline-feed-main">
            {groupedPosts.map((group, groupIndex) => {
              const barLabel = formatTimelineGroupLabel(group.posts[0].createdAt, language);
              return (
                <section
                  key={`${group.bucketKey}-${groupIndex}`}
                  className="post-timeline-group"
                  aria-label={barLabel}
                >
                  <div className="post-timeline-date-bar">{barLabel}</div>
                  <div className="post-timeline-threads">
                    {group.posts.map((post) => (
                      <div key={post.id} className="post-timeline-row">
                        <div className="post-timeline-card-wrap">
                          <PostCard
                            post={post}
                            showComments={showComments}
                            onRefreshList={onRefreshList}
                            layout="timeline"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        pageSize={pagination.size}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};

